/**
 * New campaign's calling hours as ranges per weekday (owner, 2026-10-30: "add two or three time spaces like morning
 * and evening … as many as i want and maybe different per day"). Pure, so every rule is testable without mounting.
 *
 * The form holds a {@link CallingHoursDraft} in one of two modes:
 *   - **same** ("Same hours every day"): the days to call and one list of ranges used on each of them — the old form
 *     (one range) and a morning-and-evening plan alike;
 *   - **per_day** ("Different hours each day"): each weekday its own ranges; a day with none is not called on.
 * The API receives the ranges themselves ({@link callingRangesOf}, `calling_hours`). An edit opens in **same** when every
 * calling day has the same ranges, else in **per_day** ({@link callingHoursDraftOf}).
 *
 * The rules mirror the API's (`@wetalk/domain`'s `callingHoursProblem`), worded for the form: both times `HH:MM` (an
 * end may be `24:00`, midnight at the end of the day), the start before the end, at least
 * {@link CALLING_RANGE_MINUTE_MINIMUM} minutes, and no two ranges of a day overlapping (touching is fine). There is no
 * limit on how many ranges a day has that a person would meet.
 */
import { CALLING_RANGE_MINUTE_MINIMUM } from "@wetalk/domain";
import { isRangeEnd, isRangeStart, minuteOf, rangesOfDay, type MinuteRange } from "@wetalk/view-model/calling-range";

import type { CallingRangeView } from "../../api/gateway.js";
import { portalText } from "../../i18n/portal.js";

/** One range as typed: `HH:MM` texts, unchecked. */
export interface RangeDraft {
  readonly from: string;
  readonly to: string;
}

export type CallingHoursMode = "same" | "per_day";

export interface CallingHoursDraft {
  readonly mode: CallingHoursMode;
  /** **same**: the ISO weekdays to call (Monday = 1). */
  readonly days: readonly number[];
  /** **same**: the ranges used on each of those days. */
  readonly ranges: readonly RangeDraft[];
  /** **per_day**: Monday (index 0) to Sunday (index 6), each its own ranges; empty is not called on. */
  readonly perDay: readonly (readonly RangeDraft[])[];
}

export { CALLING_RANGE_MINUTE_MINIMUM };

const WEEK = [1, 2, 3, 4, 5, 6, 7] as const;

/** The design's default: weekdays, 10:00–19:00. */
export const DEFAULT_CALLING_HOURS: CallingHoursDraft = {
  mode: "same",
  days: [1, 2, 3, 4, 5],
  ranges: [{ from: "10:00", to: "19:00" }],
  perDay: WEEK.map((day) => (day <= 5 ? [{ from: "10:00", to: "19:00" }] : [])),
};

/** A new range after the last one: from where it ends, for two hours (or to midnight). */
export function nextRangeOf(ranges: readonly RangeDraft[]): RangeDraft {
  const last = ranges.at(-1);
  if (last === undefined || !isRangeEnd(last.to)) return { from: "10:00", to: "13:00" };
  const lastTo = minuteOf(last.to);
  // Nothing fits after a range that ends this late: two empty boxes for the member to fill.
  if (lastTo > 1440 - CALLING_RANGE_MINUTE_MINIMUM) return { from: "", to: "" };
  const start = Math.min(lastTo + 60, 1440 - CALLING_RANGE_MINUTE_MINIMUM);
  return { from: timeOf(start), to: timeOf(Math.min(start + 120, 1440)) };
}

/** The ranges the API receives (`calling_hours`), by day then start. */
export function callingRangesOf(draft: CallingHoursDraft): readonly CallingRangeView[] {
  const each =
    draft.mode === "same"
      ? [...new Set(draft.days)].sort((a, b) => a - b).flatMap((day) => draft.ranges.map((one) => ({ day, one })))
      : WEEK.flatMap((day) => (draft.perDay[day - 1] ?? []).map((one) => ({ day, one })));
  return each
    .map(({ day, one }) => ({ dayOfWeek: day, fromLocalTime: one.from, toLocalTime: one.to }))
    .sort((a, b) => a.dayOfWeek - b.dayOfWeek || a.fromLocalTime.localeCompare(b.fromLocalTime));
}

/** The form's state for stored ranges: **same** when every calling day has the same ranges, else **per_day**. */
export function callingHoursDraftOf(hours: readonly CallingRangeView[]): CallingHoursDraft {
  if (hours.length === 0) return DEFAULT_CALLING_HOURS;
  const perDay = WEEK.map((day) =>
    hours
      .filter((one) => one.dayOfWeek === day)
      .sort((a, b) => a.fromLocalTime.localeCompare(b.fromLocalTime))
      .map((one) => ({ from: one.fromLocalTime, to: one.toLocalTime })),
  );
  const days = WEEK.filter((day) => (perDay[day - 1] ?? []).length > 0);
  const first = perDay[(days[0] ?? 1) - 1] ?? [];
  const same = days.every((day) => sameRanges(perDay[day - 1] ?? [], first));
  return { mode: same ? "same" : "per_day", days, ranges: first, perDay };
}

/**
 * Switching mode keeps what the member chose: **same → per_day** gives each chosen day the shared ranges; **per_day →
 * same** keeps the days that have ranges and takes the first such day's ranges for all of them.
 */
export function withMode(draft: CallingHoursDraft, mode: CallingHoursMode): CallingHoursDraft {
  if (mode === draft.mode) return draft;
  if (mode === "per_day") {
    return { ...draft, mode, perDay: WEEK.map((day) => (draft.days.includes(day) ? draft.ranges : [])) };
  }
  const days = WEEK.filter((day) => (draft.perDay[day - 1] ?? []).length > 0);
  const first = draft.perDay[(days[0] ?? 1) - 1] ?? [];
  return { ...draft, mode, days, ranges: first.length === 0 ? DEFAULT_CALLING_HOURS.ranges : first };
}

/** What is wrong with the ranges, in the form's words; `null` when they can be sent. */
export function callingHoursProblemText(hours: readonly CallingRangeView[]): string | null {
  if (hours.length === 0) return portalText("operations", "pick_at_least_one_day_to_call_abf12c4e");
  for (const one of hours) {
    if (!isRangeStart(one.fromLocalTime) || !isRangeEnd(one.toLocalTime)) {
      return portalText("operations", "write_both_times_as_hh_mm_in_24_hour_time_e0f66aee");
    }
    if (minuteOf(one.fromLocalTime) >= minuteOf(one.toLocalTime)) {
      return portalText("operations", "the_first_time_must_be_earlier_than_the_second_a_window_ca_76fcad62");
    }
    if (minuteOf(one.toLocalTime) - minuteOf(one.fromLocalTime) < CALLING_RANGE_MINUTE_MINIMUM) {
      return portalText("operations", "each_calling_range_must_be_at_least_15_minutes_long_55cf8e76");
    }
  }
  for (const day of WEEK) {
    const mine = rangesOfDay(hours, day);
    for (let step = 1; step < mine.length; step += 1) {
      if ((mine[step] as MinuteRange).from < (mine[step - 1] as MinuteRange).to) {
        return portalText("operations", "calling_hours_overlap_on_value_merge_them_into_one_range_d5ed0396", { v0: String(dayShortOf(day)) });
      }
    }
  }
  return null;
}

/**
 * The days that have at least one range, a day's ranges in minutes, and `HH:MM` in minutes: moved to
 * `@wetalk/view-model/calling-range` (plan-034 VIEW-PKG); re-exported here so no portal importer changes.
 */
export { callingDaysOf, minuteOf, rangesOfDay, type MinuteRange } from "@wetalk/view-model/calling-range";

function sameRanges(left: readonly RangeDraft[], right: readonly RangeDraft[]): boolean {
  return left.length === right.length && left.every((one, at) => one.from === right[at]?.from && one.to === right[at]?.to);
}

function timeOf(minute: number): string {
  return minute >= 1440 ? "24:00" : `${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;
}

const DAY_SHORT = () => [
  portalText("operations", "mon_24b2a099"),
  portalText("operations", "tue_529541bb"),
  portalText("operations", "wed_23408b19"),
  portalText("operations", "thu_3593ccd9"),
  portalText("operations", "fri_bbd6e32e"),
  portalText("operations", "sat_6b782d41"),
  portalText("operations", "sun_48c98cab"),
];

/** ISO weekday → "Mon". */
export function dayShortOf(isoDay: number): string {
  return DAY_SHORT()[isoDay - 1] ?? String(isoDay);
}
